import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Loader2 } from 'lucide-react';
import { businessPartsToIso, isoToBusinessParts } from './AddNewCleanDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const sel = 'h-10 w-full rounded-md border border-input bg-white px-3 text-sm';

const initial = (block, defaultStart) => {
  const s = isoToBusinessParts(block?.start_time || defaultStart);
  const e = isoToBusinessParts(block?.end_time || (defaultStart ? new Date(new Date(defaultStart).getTime() + 3600000).toISOString() : null));
  return { label: block?.label || '', who: block ? (block.scope === 'business' ? 'business' : block.cleaner_user_id) : 'business',
    date: s.date, start: s.time, endDate: e.date || s.date, end: e.time, reason: '' };
};

export default function BlockTimeDialog({ open, onOpenChange, block, defaultStart, headers, onSaved }) {
  const [f, setF] = useState(initial(block, defaultStart));
  const [team, setTeam] = useState([]);
  const [removing, setRemoving] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { if (open) { setF(initial(block, defaultStart)); setRemoving(false); setError(''); } }, [open, block, defaultStart]);
  useEffect(() => { if (open) axios.get(`${API_URL}/api/admin/schedule/blocks`, { headers }).then((r) => setTeam(r.data.team)).catch(() => {}); }, [open, headers]);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const save = async (ev) => {
    ev.preventDefault(); setError('');
    if (!f.label.trim() || !f.date || !f.start || !f.end) return setError('Fill in the label, date, start and end.');
    if (block && !f.reason.trim()) return setError('Enter a reason for the change.');
    const body = { label: f.label.trim(), scope: f.who === 'business' ? 'business' : 'cleaner', cleaner_user_id: f.who === 'business' ? null : f.who,
      start_time: businessPartsToIso(f.date, f.start), end_time: businessPartsToIso(f.endDate || f.date, f.end), reason: f.reason.trim() || null };
    setSaving(true);
    try {
      if (block) await axios.patch(`${API_URL}/api/admin/schedule/blocks/${block.id}`, body, { headers });
      else await axios.post(`${API_URL}/api/admin/schedule/blocks`, body, { headers });
      toast.success(block ? 'Blocked time updated' : 'Time blocked');
      onSaved?.(); onOpenChange(false);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : d?.message || 'Could not save the block.');
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!f.reason.trim()) return setError('Enter a reason for removing this block.');
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/admin/schedule/blocks/${block.id}/remove`, { reason: f.reason.trim() }, { headers });
      toast.success('Block removed. History kept.'); onSaved?.(); onOpenChange(false);
    } catch (err) { setError(err.response?.data?.detail || 'Could not remove the block.'); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="block-time-dialog">
        <DialogHeader>
          <DialogTitle>{block ? (removing ? 'Remove blocked time' : 'Edit blocked time') : 'Block time'}</DialogTitle>
          <DialogDescription>Keep appointments out of breaks, time off, and other commitments. Pacific time.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-3">
          {!removing && <>
            <div><Label className="text-xs">Label</Label><Input value={f.label} onChange={set('label')} maxLength={100} placeholder="Lunch, time off, holiday closure" data-testid="block-label" /></div>
            <div><Label className="text-xs">Applies to</Label>
              <select className={sel} value={f.who} onChange={set('who')} data-testid="block-who">
                <option value="business">Whole business (no one can be booked)</option>
                {team.map((t) => <option key={t.id} value={t.id}>{t.full_name}</option>)}
              </select></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Start date</Label><Input type="date" value={f.date} onChange={(e) => setF((p) => ({ ...p, date: e.target.value, endDate: p.endDate && p.endDate >= e.target.value ? p.endDate : e.target.value }))} data-testid="block-date" /></div>
              <div><Label className="text-xs">Start (PT)</Label><Input type="time" value={f.start} onChange={set('start')} data-testid="block-start" /></div>
              <div><Label className="text-xs">End date</Label><Input type="date" value={f.endDate} min={f.date} onChange={set('endDate')} data-testid="block-end-date" /></div>
              <div><Label className="text-xs">End (PT)</Label><Input type="time" value={f.end} onChange={set('end')} data-testid="block-end" /></div>
            </div>
          </>}
          {block && <div><Label className="text-xs">{removing ? 'Reason for removal' : 'Reason for change'}</Label><Input value={f.reason} onChange={set('reason')} maxLength={300} data-testid="block-reason" /></div>}
          {error && <p className="text-sm text-red-600" data-testid="block-error">{error}</p>}
          <div className="flex flex-wrap justify-between gap-2 pt-1">
            {block && !removing && <Button type="button" variant="ghost" className="text-red-700" onClick={() => { setRemoving(true); setError(''); }} data-testid="block-remove-btn">Remove block</Button>}
            {removing ? <Button type="button" className="ml-auto bg-red-700 hover:bg-red-800 text-white" onClick={remove} disabled={saving} data-testid="block-confirm-remove-btn">{saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Confirm removal</Button>
              : <Button type="submit" className="ml-auto bg-[#F56F05] hover:bg-[#C2410C] text-white" disabled={saving} data-testid="block-save-btn">{saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}{block ? 'Save changes' : 'Block time'}</Button>}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
